import type { RootState } from '@reducers/index';
import {
  RadiusSearch,
  setRadiusDialogStatus,
  updateRadiusSearch,
} from '@reducers/search';
import { setPreventPopstate } from '@reducers/system';
import { useDebounce } from '@sbt-web/hooks';
import { HereGeocodeItem, HereMapsClient } from '@sbt-web/networking';
import { Pulse } from '@sbt-web/tracking';
import {
  Button,
  FormGroup,
  FormLabel,
  FullModal,
  Slider,
  TextField,
} from '@sbt-web/ui';
import { formatDistance } from '@sbt-web/utils';
import { captureException } from '@sentry/browser';
import { getOrCreatePulse } from '@tools/tracking/utils';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { batch, connect } from 'react-redux';
import FilterMap from './FilterMap';
import ownClasses from './index.module.scss';
import SearchMap from './SearchMap';
import {
  buildRadiusSearchCancelEvent,
  buildRadiusSearchInitiateEvent,
} from './tracking';
import { FilterURI } from '@sbt-web/network/types';

const defaultRadius = 1000;

interface MapModalProps {
  handleSelection: (selection: RadiusSearch) => void;
  isOpen: boolean;
  onClose: () => void;
  radiusSearch?: RadiusSearch;
}

interface RadiusPickerProps {
  enabled: boolean;
  startingRadius?: number;
  setRadius: (radius: number) => void;
}

interface StateProps {
  radiusSearch?: RadiusSearch;
}

interface DispatchProps {
  updateRadiusParameters: (radiusSearch: RadiusSearch) => void;
  updateRadiusDialogStatus: (isOpen: boolean) => void;
}

interface InputProps {
  value: string;
  name?: string;
  onSelect: (item: HereGeocodeItem) => void;
  pulse?: Pulse;
}

interface OwnProps {
  trackSearchConversion: () => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const useHere = (
  hereApiKey: string,
  debounceTimeout: number
): [HereGeocodeItem[], (query?: string) => void] => {
  const [items, setItems] = useState<HereGeocodeItem[]>([]);
  const [query, setQuery] = useState<string>('');

  // The query can be repeatedly updated but only the debounced
  // value will ever be set, to trigger useEffect.
  const debouncedQuery = useDebounce(query, debounceTimeout);

  const updateQuery = useCallback(
    (q = '') => {
      setQuery(q);
    },
    [setQuery]
  );

  const client = useMemo(
    () => new HereMapsClient({ apiKey: hereApiKey }),
    [hereApiKey]
  );

  useEffect(() => {
    if (debouncedQuery.length >= 2) {
      client
        .geocode(debouncedQuery)
        .then((res) => {
          if (res.payload != undefined) {
            setItems(res.payload);
          } else {
            throw new Error('Here payload was empty.');
          }
        })
        .catch(console.error);
    } else {
      setItems([]);
    }
  }, [client, debouncedQuery]);

  return [items, updateQuery];
};

const textFieldRef = createRef<HTMLInputElement>();
const debounceTimeout = 250;

const GeoSearchInput = ({ value, name, onSelect }: InputProps) => {
  const [options, setOptions] = useState<HereGeocodeItem[]>([]);

  const [hereItems, updateHereItems] = useHere(
    process.env.NEXT_PUBLIC_HERE_WEB_KEY,
    debounceTimeout
  );

  useEffect(() => {
    textFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    setOptions(hereItems);
  }, [hereItems, setOptions]);

  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    inputValue,
    isOpen,
    openMenu,
    // selectedItem,
    setInputValue,
  } = useCombobox<HereGeocodeItem>({
    id: 'desktop-modal-geo-search',
    items: options,
    initialInputValue: value,
    itemToString: (item) => item?.title ?? '',
    onInputValueChange: (changes) => {
      updateHereItems(changes.inputValue);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem == null) {
        return;
      }
      textFieldRef.current?.blur();
      onSelect(selectedItem);
    },
    onStateChange: (changes) => {
      if (
        changes.type === useCombobox.stateChangeTypes.ItemClick &&
        changes.selectedItem == null
      ) {
        // Only intervene if the selected item hasn't changed.
        // Otherwise let Downshift do the work.
        textFieldRef.current?.blur();
      }
    },
  });

  const clearInput = useCallback(() => {
    setInputValue('');
    textFieldRef.current?.focus();
  }, [setInputValue]);

  return (
    <div className={ownClasses['geo-search-wrapper']}>
      <div {...getComboboxProps()}>
        <TextField
          size="large"
          icon="Pin"
          withClearIcon={inputValue?.length > 0}
          onInputChange={(e) => setInputValue(e.target.value)}
          onCleared={clearInput}
          disableNativeAC
          {...getInputProps({
            ref: textFieldRef,
            type: 'search',
            placeholder: 'Indirizzo, quartiere, città, o CAP',
            name,
            value: inputValue,
            onFocus: () => {
              openMenu();
            },
          })}
        />
      </div>
      <div
        className={classNames(ownClasses['dropdown-wrapper'], {
          [ownClasses.open]: isOpen && options.length > 0,
        })}
      >
        <ul
          className={classNames(ownClasses.dropdown, ownClasses.results)}
          {...getMenuProps()}
        >
          {isOpen &&
            options.map((item, index: number) => (
              <li
                className={classNames(
                  ownClasses.item,
                  ownClasses['size-small'],
                  {
                    [ownClasses.selected]: highlightedIndex === index,
                  }
                )}
                key={`${item.title}${index}`}
                {...getItemProps({ item, index })}
              >
                {item.title}
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
};

const MapModal: React.FunctionComponent<MapModalProps> = ({
  radiusSearch,
  handleSelection,
  isOpen,
  onClose,
}) => {
  const [prevSelection, setPrevSelection] = useState<
    HereGeocodeItem | undefined
  >(undefined);

  const [selectedPoint, setSelectedPoint] = useState<
    HereGeocodeItem | undefined
  >(() => {
    if (radiusSearch != undefined) {
      return {
        ...(prevSelection ?? {}),
        title: prevSelection?.title ?? 'Il punto che hai selezionato',
        position: {
          lat: radiusSearch?.center.lat,
          lng: radiusSearch?.center.lng,
        },
        address: prevSelection?.address ?? {},
      };
    } else {
      return undefined;
    }
  });

  const [radius, setRadius] = useState(() => {
    if (radiusSearch?.radiusMeters) {
      return radiusSearch.radiusMeters;
    } else {
      return defaultRadius;
    }
  });

  useEffect(() => {
    if (!radiusSearch) {
      setPrevSelection(undefined);
      setSelectedPoint(undefined);
    }
  }, [radiusSearch]);

  useEffect(() => {
    if (radiusSearch && prevSelection == undefined) {
      setPrevSelection(selectedPoint);
    }
  }, [radiusSearch, prevSelection, selectedPoint]);

  const [searchApplied, setSearchApplied] = useState(false);

  return (
    <FullModal
      open={isOpen}
      size="small"
      withClose
      onClose={() => {
        setSelectedPoint(prevSelection);
        onClose();

        if (searchApplied) {
          setSearchApplied(false);
        } else {
          getOrCreatePulse()?.queueEvent(buildRadiusSearchCancelEvent());
        }
      }}
      title="Dove stai cercando?"
      actions={[
        <Button
          key="confirm-radius"
          design="solid"
          disabled={selectedPoint == undefined}
          onClick={() => {
            if (
              selectedPoint != undefined &&
              selectedPoint.position != undefined
            ) {
              handleSelection({
                radiusMeters: radius,
                center: {
                  lat: selectedPoint.position.lat,
                  lng: selectedPoint.position.lng,
                },
                friendlyName: selectedPoint.title,
                addressToTrack: selectedPoint.address,
              });
              setPrevSelection(selectedPoint);
              setSearchApplied(true);
              onClose();
            } else {
              const errorMessage =
                'The selected point in the desktop dialog was undefined when applying the search.';
              captureException(new Error(errorMessage), { level: 'error' });
              console.error(errorMessage);
            }
          }}
        >
          Applica
        </Button>,
      ]}
    >
      <div className={ownClasses['modal-content-wrapper']}>
        <GeoSearchInput
          value={selectedPoint?.title ?? ''}
          onSelect={setSelectedPoint}
          name={FilterURI.Geo}
          pulse={getOrCreatePulse()}
        />

        <SearchMap radius={radius} selectedPoint={selectedPoint} />

        <RadiusPicker
          startingRadius={radiusSearch?.radiusMeters}
          setRadius={setRadius}
          enabled
        />
      </div>
    </FullModal>
  );
};

const RadiusPicker = ({
  enabled,
  startingRadius = 1000,
  setRadius,
}: RadiusPickerProps) => {
  const onRadiusChange = useCallback(
    (newRadius: number) => {
      setRadius(newRadius);
    },
    [setRadius]
  );

  useEffect(() => {
    setRadius(startingRadius);
  }, [setRadius, startingRadius]);

  return (
    <div className={ownClasses['radius-picker']}>
      <p
        className={classNames(
          'body-text semibold',
          ownClasses['radius-picker-label']
        )}
      >
        Seleziona il raggio
      </p>

      <Slider
        disabled={!enabled}
        min={500}
        max={10000}
        defaultValue={startingRadius}
        step={500}
        onChangeValue={onRadiusChange}
        labelBuilder={formatDistance}
        withLabel
      />
    </div>
  );
};

const RadiusSelector: React.FunctionComponent<Props> = ({
  radiusSearch,
  updateRadiusParameters,
  updateRadiusDialogStatus,
  trackSearchConversion,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const applyRadius = useCallback(
    (selection: RadiusSearch) => {
      updateRadiusParameters(selection);
      trackSearchConversion();
    },
    [updateRadiusParameters, trackSearchConversion]
  );

  const ctaLabel =
    radiusSearch != undefined ? 'Modifica raggio' : 'Cerca per distanza';

  return (
    <FormGroup>
      <div className={classNames(ownClasses['radius-filter-container'])}>
        <FormLabel>Distanza da un punto</FormLabel>
      </div>
      <FilterMap
        radius={radiusSearch?.radiusMeters ?? 1000}
        selectedPoint={
          radiusSearch != undefined
            ? {
                position: {
                  lat: radiusSearch.center.lat,
                  lng: radiusSearch.center.lng,
                },
              }
            : undefined
        }
      />

      <Button
        design="solid-lite"
        onClick={() => {
          updateRadiusDialogStatus(true);
          setIsDialogOpen(true);
          getOrCreatePulse()?.queueEvent(
            buildRadiusSearchInitiateEvent({ label: ctaLabel })
          );
        }}
        classes={[ownClasses['dialog-entry-point']]}
      >
        {ctaLabel}
      </Button>

      <MapModal
        handleSelection={applyRadius}
        isOpen={isDialogOpen}
        onClose={() => {
          updateRadiusDialogStatus(false);
          setIsDialogOpen(false);
        }}
        radiusSearch={radiusSearch}
      />
    </FormGroup>
  );
};

const ConnectedRadiusSelector = connect<
  StateProps,
  DispatchProps,
  OwnProps,
  RootState
>(
  (state, ownProps) => ({
    radiusSearch: state.search.radiusSearch || undefined,
    trackSearchConversion: ownProps.trackSearchConversion,
  }),
  (dispatch) => ({
    updateRadiusParameters: (radiusSearch: RadiusSearch) => {
      batch(() => {
        dispatch(setPreventPopstate(false));
        dispatch(setRadiusDialogStatus(false));
        dispatch(updateRadiusSearch({ radiusSearch, isFinalSearch: true }));
      });
    },
    updateRadiusDialogStatus: (isOpen: boolean) => {
      batch(() => {
        dispatch(setPreventPopstate(isOpen));
        dispatch(setRadiusDialogStatus(isOpen));
      });
    },
  })
)(RadiusSelector);

ConnectedRadiusSelector.displayName = 'RadiusSelector';

export default ConnectedRadiusSelector;
